/**
 * useDailyChallenge Hook
 * 
 * Manages daily challenges with special rewards and goals.
 * 
 * @module useDailyChallenge
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useGameProgress } from './useGameProgress';
import { storage, STORAGE_KEYS } from '../utils/storage';
import { logger } from '../utils/logger';

/**
 * Challenge types
 */
export const CHALLENGE_TYPES = {
  COMPLETE_SUBJECT: 'complete_subject',
  ANSWER_CORRECTLY: 'answer_correctly',
  MAINTAIN_STREAK: 'maintain_streak',
  TIME_LIMIT: 'time_limit',
  PERFECT_SCORE: 'perfect_score'
};

/**
 * Hook for managing daily challenges
 * 
 * @returns {Object} Challenge state and management functions
 */
/**
 * Load today's challenge from storage (helper function)
 * Uses localStorage directly to avoid module import
 * 
 * @returns {Object|null} Challenge or null
 */
const loadDailyChallengeFromStorage = () => {
  try {
    const saved = localStorage.getItem(`emmy-${STORAGE_KEYS.DAILY_CHALLENGE}`);
    if (!saved) return null;
    const parsed = JSON.parse(saved);
    const today = new Date().toISOString().split('T')[0];
    
    if (parsed && parsed.date === today) {
      return parsed;
    }
    
    return null;
  } catch (error) {
    // Silently fail - logging would require module import
    return null;
  }
};

export const useDailyChallenge = () => {
  const { progress, saveProgress } = useGameProgress();
  
  // Cache lazy-loaded modules
  // Storage and logger are now statically imported
  
  const [challenge, setChallenge] = useState(() => {
    return loadDailyChallengeFromStorage();
  });

  const [challengeProgress, setChallengeProgress] = useState(() => {
    return challenge?.progress || {};
  });

  /**
   * Generate a new daily challenge
   * 
   * @returns {Object} Challenge object
   */
  const generateChallenge = useCallback(() => {
    const challenges = [
      {
        id: `challenge-${Date.now()}`,
        type: CHALLENGE_TYPES.COMPLETE_SUBJECT,
        title: 'Complete a Subject',
        description: 'Complete any subject game to earn bonus points!',
        target: 1,
        reward: { points: 50, badge: 'daily-completer' },
        progress: { current: 0 }
      },
      {
        id: `challenge-${Date.now()}`,
        type: CHALLENGE_TYPES.ANSWER_CORRECTLY,
        title: 'Answer 10 Questions Correctly',
        description: 'Get 10 questions right in a row!',
        target: 10,
        reward: { points: 30, badge: 'accuracy-master' },
        progress: { current: 0, streak: 0 }
      },
      {
        id: `challenge-${Date.now()}`,
        type: CHALLENGE_TYPES.PERFECT_SCORE,
        title: 'Get a Perfect Score',
        description: 'Answer all questions correctly in one game!',
        target: 1,
        reward: { points: 100, badge: 'perfectionist' },
        progress: { current: 0 }
      }
    ];

    // Randomly select a challenge
    const selected = challenges[Math.floor(Math.random() * challenges.length)];
    selected.date = new Date().toISOString().split('T')[0];
    
    return selected;
  }, []);

  /**
   * Load today's challenge from storage
   * 
   * @returns {Object|null} Challenge or null
   */
  const loadDailyChallenge = useCallback(() => {
    const saved = storage.get(STORAGE_KEYS.DAILY_CHALLENGE);
    const today = new Date().toISOString().split('T')[0];
    
    if (saved && saved.date === today) {
      return saved;
    }
    
    // Generate new challenge for today
    const newChallenge = generateChallenge();
    storage.set(STORAGE_KEYS.DAILY_CHALLENGE, newChallenge);
    return newChallenge;
  }, [generateChallenge]);

  /**
   * Check if challenge is completed
   * 
   * @param {Object} currentProgress - Current progress
   * @returns {boolean} Completion status
   */
  const checkCompletion = useCallback((currentProgress) => {
    if (!challenge) return false;

    switch (challenge.type) {
      case CHALLENGE_TYPES.COMPLETE_SUBJECT:
        return currentProgress.current >= challenge.target;
      case CHALLENGE_TYPES.ANSWER_CORRECTLY:
        return currentProgress.streak >= challenge.target;
      case CHALLENGE_TYPES.PERFECT_SCORE:
        return currentProgress.current >= challenge.target;
      default:
        return false;
    }
  }, [challenge]);

  /**
   * Complete the challenge and award rewards
   */
  const completeChallenge = useCallback((currentProgress) => {
    if (!challenge || challenge.completed) return;

    const completedChallenge = {
      ...challenge,
      completed: true,
      completedAt: new Date().toISOString(),
      progress: currentProgress || challengeProgress
    };

    // Award rewards
    const newUserProgress = {
      ...progress,
      totalScore: (progress?.totalScore || 0) + (challenge.reward?.points || 0)
    };

    if (challenge.reward?.badge && newUserProgress.achievements) {
      if (!newUserProgress.achievements.includes(challenge.reward.badge)) {
        newUserProgress.achievements.push(challenge.reward.badge);
      }
    }

    saveProgress(newUserProgress);

    // Save completed challenge (lazy load storage)
    getStorage().then(storage => {
      storage.set(STORAGE_KEYS.DAILY_CHALLENGE, completedChallenge);
      setChallenge(completedChallenge);
      getLogger().then(logger => logger.info('Daily challenge completed!', challenge)).catch(() => {});
    }).catch(() => {
      // Silently fail - fallback to localStorage
      try {
        localStorage.setItem(`emmy-${STORAGE_KEYS.DAILY_CHALLENGE}`, JSON.stringify(completedChallenge));
        setChallenge(completedChallenge);
      } catch (e) {
        // Silently fail
      }
    });
  }, [challenge, progress, saveProgress, challengeProgress]);

  /**
   * Update challenge progress
   * 
   * @param {Object} update - Progress update
   */
  const updateProgress = useCallback((update) => {
    if (!challenge) return;

    const newProgress = {
      ...challengeProgress,
      ...update
    };

    setChallengeProgress(newProgress);

    // Check if challenge is completed
    const isCompleted = checkCompletion(newProgress);
    
    if (isCompleted && !challenge.completed) {
      completeChallenge(newProgress);
    }
  }, [challenge, challengeProgress, checkCompletion, completeChallenge]);

  /**
   * Get challenge progress percentage
   */
  const progressPercentage = useMemo(() => {
    if (!challenge) return 0;

    switch (challenge.type) {
      case CHALLENGE_TYPES.COMPLETE_SUBJECT:
      case CHALLENGE_TYPES.PERFECT_SCORE:
        return Math.min((challengeProgress.current / challenge.target) * 100, 100);
      case CHALLENGE_TYPES.ANSWER_CORRECTLY:
        return Math.min((challengeProgress.streak / challenge.target) * 100, 100);
      default:
        return 0;
    }
  }, [challenge, challengeProgress]);

  return {
    challenge,
    progress: challengeProgress,
    updateProgress,
    completeChallenge,
    progressPercentage,
    isCompleted: challenge?.completed || false
  };
};

